import type { Transaction } from '@api/hooks/types/getTransactions';
import { PageContainer } from '@components/PageContainer';
import { PermissionGate } from '@components/PermissionGate';
import { PaymentTfa, SigningStatus } from '@components/tfa/PaymentTfa';
import { Alert, AlertType, Card, Typography } from '@gurupay/gp-ui-library';
import { useFF } from '@hooks/useFF.ts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CC_FEATURE_FLAGS } from 'types/layout.ts';
import { OperationStatus } from 'types/operations';
import { Capability } from 'types/roles';

import Actions from './components/SpecificPayment/Actions';
import { SpecificPaymentCardItem } from './components/SpecificPayment/SpecificPaymentCardItem';
import { getPaymentDetailCards } from './components/SpecificPayment/SpecificPaymentConfig';

interface SpecificPaymentProps {
	operation: Transaction;
}

export const SpecificPayment = ({ operation }: SpecificPaymentProps) => {
	const { t } = useTranslation('', { keyPrefix: 'payments.specificPayment' });
	const hasAddressAligned = useFF(CC_FEATURE_FLAGS.ADDRESS_ALIGNMENT);
	const paymentCards = getPaymentDetailCards(operation, t, hasAddressAligned);
	const [signingStatus, setSigningStatus] = useState<SigningStatus | undefined>();

	const isStatusCreated = operation.status === OperationStatus.CREATED;

	const isTFAShown =
		!operation.batch_id &&
		operation?.status === OperationStatus.CREATED &&
		signingStatus !== SigningStatus.SUCCESS;

	return (
		<PageContainer backUrl="/operations" title={t('title')}>
			<div className="flex flex-col gap-y-space-40 max-w-[512px] mx-auto">
				{!isStatusCreated && <Actions operation={operation} />}

				{paymentCards.map((card) => (
					<Card key={card.title} className="w-full" title={card.title}>
						<div className="flex flex-col gap-space-24">
							{card.groups.map((group, groupIndex) => (
								<div
									key={group.title ?? groupIndex}
									className="flex flex-col gap-space-16"
								>
									{group.title && (
										<Typography className="font-bold" level="small-text">
											{group.title}
										</Typography>
									)}

									{group.fields.map((field) => (
										<SpecificPaymentCardItem
											key={field.title}
											canCopy={field.canCopy}
											content={field.content}
											title={field.title}
										/>
									))}

									{groupIndex < card.groups.length - 1 && (
										<div className="w-full bg-neutral-100 h-px" />
									)}
								</div>
							))}
						</div>
					</Card>
				))}

				{isTFAShown && (
					<PermissionGate capability={Capability.SIGN_PAYMENT}>
						<PaymentTfa
							onSign={setSigningStatus}
							showSignLaterButton={false}
							transactionId={operation?.id}
						/>
					</PermissionGate>
				)}

				<div>
					{signingStatus === SigningStatus.SUCCESS && (
						<Alert type={AlertType.SUCCESS}>{t('alerts.signed')}</Alert>
					)}
					{signingStatus === SigningStatus.ERROR && (
						<Alert type={AlertType.ERROR}>{t('alerts.signingFailed')}</Alert>
					)}
				</div>
			</div>
		</PageContainer>
	);
};
